/**
 * useBusinessSearch - React Query hook for searching businesses by name
 *
 * Sprint 043: Admin Business Correction Workflow
 *
 * Provides a debounced search hook for the ReassignBusinessModal.
 * Wraps GET /api/businesses/search endpoint.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/infrastructure/api/ApiClient';

/**
 * Business search result matching backend BusinessSearchResult DTO
 */
export interface BusinessSearchResult {
  id: string;
  name: string;
  displayName: string;
  category: string | null;
  website: string | null;
  isActive: boolean;
  relevanceScore: number;
  regionId: string;
  regionName: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  formattedAddress: string | null;
  companyId: string | null;
  companyName: string | null;
}

interface BusinessSearchApiResponse {
  results: BusinessSearchResult[];
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

/**
 * useBusinessSearch - Query hook for searching businesses by name
 *
 * Features:
 * - Minimum 2 character query required
 * - Returns paginated results sorted by relevance
 * - Automatically disabled when query is too short
 *
 * @param query - Search query string
 * @param regionId - Optional region ID to filter results
 * @param enabled - Whether the query should run (default: true)
 *
 * @example
 * const { data, isLoading } = useBusinessSearch(debouncedQuery, regionId);
 * // data?.results contains BusinessSearchResult[]
 */
export function useBusinessSearch(
  query: string,
  regionId?: string,
  enabled: boolean = true
) {
  const trimmedQuery = query.trim();
  const shouldSearch = trimmedQuery.length >= 2 && enabled;

  return useQuery<BusinessSearchApiResponse>({
    queryKey: ['businessSearch', trimmedQuery, regionId],
    queryFn: async (): Promise<BusinessSearchApiResponse> => {
      const params = new URLSearchParams({ q: trimmedQuery, pageSize: '20' });
      if (regionId) {
        params.append('regionId', regionId);
      }
      return apiClient.get<BusinessSearchApiResponse>(
        `/api/businesses/search?${params.toString()}`
      );
    },
    enabled: shouldSearch,
    staleTime: 30_000,
    gcTime: 60_000
  });
}
